/**
 * Comments API - Create Operations
 *
 * Adding new comments to posts.
 */

import {
  collection,
  doc,
  getDoc,
  writeBatch,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../../firebaseConfig";
import { ensureFreshToken } from "../authUtils";

/**
 * Creates a new comment on a post.
 * Uses a Firestore batch so the comment write and counter increments are atomic.
 *
 * Increments TWO counters:
 * - `commentCount`: Total activity (comments + replies) - used for display badges
 * - `topLevelCommentCount`: Top-level comments only - used for pagination
 *
 * @param {string} postId
 * @param {{ text: string, username: string, profession?: string, photoURL?: string, userId?: string }} comment
 * @returns {Promise<{ id: string, postId: string, userId: string, username: string, profession: string, photoURL: string, text: string, timestamp: Date, likes: number, replyCount: number, isOptimistic: false }>}
 *          The complete comment object with server-generated ID and client timestamp.
 */
export const addComment = async (postId, comment) => {
  try {
    // ── Phase 1: Data Sanitization (Source of Truth) ──
    const trimmedText = (comment.text || "").trim();

    if (!trimmedText) {
      throw new Error("Comment text cannot be empty");
    }
    if (trimmedText.length > 2000) {
      throw new Error(
        `Comment exceeds maximum length of 2000 characters (got ${trimmedText.length})`,
      );
    }

    await ensureFreshToken();

    const postSnap = await getDoc(doc(db, "posts", postId));
    if (!postSnap.exists()) {
      throw new Error("Post not found");
    }
    if (postSnap.data()?.status === "deleted") {
      throw new Error("Cannot comment on a deleted post");
    }

    // Verify auth state AFTER token refresh
    const authUser = auth?.currentUser;
    if (!authUser) {
      throw new Error("User not authenticated after token refresh");
    }

    const commentsRef = collection(db, "comments");
    const commentRef = doc(commentsRef);
    const postRef = doc(db, "posts", postId);

    // Always use Firebase auth UID as the authoritative source.
    const authUid = authUser.uid;

    // ATOMIC BATCH: Comment write + dual counter increment
    const batch = writeBatch(db);
    batch.set(commentRef, {
      postId,
      username: comment.username,
      userProfession: comment.profession || "",
      userPhotoURL: comment.photoURL || "",
      text: trimmedText,
      userId: authUid, // Always Firebase auth UID — matches security rules
      timestamp: serverTimestamp(),
      likes: 0,
    });
    // Counter 1: Total activity (comments + replies) - for display badges
    batch.update(postRef, { commentCount: increment(1) });
    // Counter 2: Top-level comments only - for pagination logic
    batch.update(postRef, { topLevelCommentCount: increment(1) });
    await batch.commit();

    // Return the full comment object for optimistic UI reconciliation
    return {
      id: commentRef.id,
      postId,
      userId: authUid,
      username: comment.username || "User",
      userProfession: comment.profession || "",
      profession: comment.profession || "",
      photoURL: comment.photoURL || "",
      text: trimmedText,
      timestamp: new Date(),
      likes: 0,
      replyCount: 0,
      isOptimistic: false,
      isEdited: false,
      updatedAt: null,
      deletedAt: null,
    };
  } catch (error) {
    if (error?.code === "permission-denied") {
      console.error(
        "[addComment] permission-denied — ensure Firebase Auth is active and " +
          "rules allow comment creation.",
        { isAuthenticated: !!auth?.currentUser },
      );
    } else {
      console.error("Error adding comment:", error);
    }
    throw error;
  }
};
